import { NOTIFI_PASTEL_COLORS } from '@/lib/design/brand-colors';
import { useState } from 'react';
import { Check, ChevronDown, X, Plus, Tag as TagIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { SectionEmptyState } from '@/components/molecules/SectionEmptyState';
import { AddRow, DetailCard } from '@/components/organisms/contacts/detail/_shared';
import { EntityTagDialog } from '@/components/organisms/EntityTagDialog';
import { useTags, useCreateTag } from '@/hooks/tags/useTags';
import type { Tag } from '@/types/estimate';

interface TagSelectorProps {
  scope: string;
  value: string[];
  onChange: (tagIds: string[]) => void;
  allowCreate?: boolean;
  emptyMessage?: string;
  searchPlaceholder?: string;
  variant?: 'inline' | 'input' | 'section';
  triggerId?: string;
  /** section variant — empty-state description + read-only gating. */
  emptyDescription?: string;
  canEdit?: boolean;
}

export function TagSelector({
  scope,
  value,
  onChange,
  allowCreate = true,
  emptyMessage = 'No tags yet',
  searchPlaceholder,
  variant = 'inline',
  triggerId,
  emptyDescription = 'Group and filter with tags.',
  canEdit = true,
}: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [color, setColor] = useState<string>(NOTIFI_PASTEL_COLORS[0]!);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: allTags = [], isLoading } = useTags(scope);
  const createTag = useCreateTag(scope);

  const selectedTags = allTags.filter((t) => value.includes(t.id));
  const availableTags = allTags.filter(
    (t) => !value.includes(t.id) && t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const exactMatch = allTags.find((t) => t.name.toLowerCase() === search.toLowerCase());

  function toggleTag(tag: Tag) {
    if (!canEdit) return;
    if (value.includes(tag.id)) {
      onChange(value.filter((id) => id !== tag.id));
    } else {
      onChange([...value, tag.id]);
    }
  }

  function removeTag(tagId: string) {
    if (!canEdit) return;
    onChange(value.filter((id) => id !== tagId));
  }

  async function handleCreateTag() {
    if (!canEdit || !allowCreate || createTag.isPending || !search.trim() || exactMatch) return;
    const created = await createTag.mutateAsync({ name: search.trim(), color }).catch(() => null);
    if (!created) return;
    // Only select what actually came back with an id — appending a blank puts
    // `undefined` in the list, which serialises to `null` and makes the API
    // reject the whole save over one unusable tag.
    if (created?.id) onChange([...value, created.id]);
    setSearch('');
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen && canEdit);
    if (!nextOpen) {
      setSearch('');
      setColor(NOTIFI_PASTEL_COLORS[0]!);
    }
  }

  const tagBadges = selectedTags.map((tag) => (
    <Badge
      key={tag.id}
      variant="secondary"
      className="flex max-w-full items-center gap-1 pr-1 text-xs"
      style={tag.color ? { backgroundColor: `${tag.color}20`, borderColor: tag.color } : undefined}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: tag.color ?? 'hsl(var(--muted-foreground))' }}
      />
      <span className="max-w-[200px] truncate">{tag.name}</span>
      {canEdit && (
        <button
          type="button"
          onClick={() => removeTag(tag.id)}
          className="ml-0.5 shrink-0 rounded-full transition-colors hover:text-destructive"
        >
          <X size={10} />
        </button>
      )}
    </Badge>
  ));

  const dropdownContent = (
    <>
      <div className="border-b p-2">
        <Input
          autoFocus
          placeholder={
            searchPlaceholder ?? (allowCreate ? 'Search or create tag...' : 'Search tags...')
          }
          className="h-7 text-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleCreateTag();
            }
            if (e.key === 'Escape') handleOpenChange(false);
          }}
        />
      </div>

      <div className="max-h-48 overflow-y-auto p-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={14} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {availableTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: tag.color ?? 'hsl(var(--muted-foreground))' }}
                />
                <span className="min-w-0 flex-1 truncate">{tag.name}</span>
              </button>
            ))}

            {allowCreate && search.trim() && !exactMatch && (
              <div className="mt-2 border-t pt-2">
                <p className="mb-2 px-2 text-xs font-medium">Tag color</p>
                <div className="flex flex-wrap gap-1.5 px-2 pb-2">
                  {NOTIFI_PASTEL_COLORS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-label={`Color ${option}`}
                      aria-pressed={color === option}
                      onClick={() => setColor(option)}
                      className={`flex size-6 items-center justify-center rounded-full border-2 ${color === option ? 'border-foreground' : 'border-transparent'}`}
                      style={{ backgroundColor: option }}
                    >
                      {color === option && <Check className="size-3 text-black" />}
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-0.5 w-full min-w-0 justify-start gap-1.5 text-xs"
                  onClick={() => void handleCreateTag()}
                  disabled={createTag.isPending}
                >
                  {createTag.isPending ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Plus size={11} />
                  )}
                  <span className="min-w-0 truncate">Create &quot;{search.trim()}&quot;</span>
                </Button>
              </div>
            )}

            {availableTags.length === 0 && !search.trim() && (
              <p className="py-3 text-center text-xs text-muted-foreground">{emptyMessage}</p>
            )}

            {availableTags.length === 0 && search.trim() && !allowCreate && (
              <p className="py-3 text-center text-xs text-muted-foreground">No matching tags</p>
            )}
          </>
        )}
      </div>
    </>
  );

  if (variant === 'section') {
    // Detail-page card matching EntityCustomFieldsSection: titled DetailCard, a bare
    // SectionEmptyState when empty, chips + an "Add tag" row when populated. The Add
    // affordance opens a modal form (EntityTagDialog) like Custom Fields, not an
    // inline dropdown. AddRow carries its own top border — no extra divider wrapper.
    return (
      <>
        <DetailCard title="Tags">
          {value.length > 0 ? (
            // Populated is decided by the assigned ids, not resolved metadata, so the
            // card never flashes "No tags" while useTags is still loading (or fails) —
            // the chips just fill in as the tag metadata resolves.
            <>
              <div className="flex flex-wrap gap-2 px-4 py-3.5">{tagBadges}</div>
              {canEdit && <AddRow label="Add tag" onClick={() => setDialogOpen(true)} />}
            </>
          ) : (
            <SectionEmptyState
              variant="bare"
              icon={TagIcon}
              title="No tags"
              description={emptyDescription}
              {...(canEdit
                ? { ctaLabel: 'Add tag', onCtaClick: () => setDialogOpen(true) }
                : { hideCta: true })}
            />
          )}
        </DetailCard>
        <EntityTagDialog
          scope={scope}
          value={value}
          onChange={onChange}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </>
    );
  }

  if (!canEdit) {
    return <div className="flex min-w-0 flex-wrap items-center gap-1.5">{tagBadges}</div>;
  }

  if (variant === 'inline') {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {tagBadges}
        {canEdit && (
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <TagIcon size={12} />
                Add tags
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" collisionPadding={8} className="w-56 p-0">
              {dropdownContent}
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-2">
      {selectedTags.length > 0 ? (
        <div className="flex min-w-0 flex-wrap gap-1.5">{tagBadges}</div>
      ) : null}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            id={triggerId}
            type="button"
            className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/30"
          >
            <TagIcon size={14} />
            <span className="flex-1 text-left">
              {value.length === 0
                ? 'Add tags...'
                : `${value.length} tag${value.length === 1 ? '' : 's'} selected`}
            </span>
            <ChevronDown size={14} className="opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={8}
          className="w-[--radix-popover-trigger-width] min-w-56 p-0"
        >
          {dropdownContent}
        </PopoverContent>
      </Popover>
    </div>
  );
}
